import {
  HoldingValidationError,
  validateDividendDraft,
  type Holding,
  type ValidatedDividend,
} from '@porttang/core';
import { useMemo, useState } from 'react';

const inputClass =
  'w-full rounded-md border border-line-strong bg-sunken px-3 py-2 text-ink-100 outline-none focus:border-brand';

type Result =
  | { state: 'idle' }
  | { state: 'error'; message: string }
  | { state: 'ok'; dividend: ValidatedDividend };

/**
 * บันทึกปันผลที่ได้รับ — เลือกได้เฉพาะของที่ยังถืออยู่
 *
 * ปันผลของที่ขายหมดไปแล้วบันทึกไม่ได้ เพราะต้องมี holding ให้คัดลอกชื่อ/แพลตฟอร์มมา
 * กรณีนี้เกิดน้อย (ปันผลมักเข้าก่อนขาย) จึงไม่เพิ่มฟอร์มกรอกชื่อเองให้ซับซ้อน
 */
export function DividendForm({
  holdings,
  isSaving,
  onCancel,
  onSubmit,
}: {
  /** ต้องไม่มีเงินสดปนมา และต้องมีอย่างน้อยหนึ่งรายการ */
  holdings: readonly Holding[];
  isSaving: boolean;
  onCancel: () => void;
  onSubmit: (dividend: ValidatedDividend) => void;
}) {
  const [holdingId, setHoldingId] = useState(holdings[0]?.id ?? '');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState(holdings[0]?.currency ?? '');
  const [note, setNote] = useState('');

  const holding = holdings.find((item) => item.id === holdingId);

  const result = useMemo<Result>(() => {
    if (holding === undefined || amount.trim() === '') {
      return { state: 'idle' };
    }
    try {
      return { state: 'ok', dividend: validateDividendDraft(holding, { amount, currency, note }) };
    } catch (caught) {
      if (caught instanceof HoldingValidationError) {
        return { state: 'error', message: caught.message };
      }
      throw caught;
    }
  }, [holding, amount, currency, note]);

  function chooseHolding(id: string) {
    setHoldingId(id);
    // สกุลเงินตั้งต้นตามต้นทุนของตัวที่เลือก แต่ยังแก้ได้ — โบรกบางเจ้าจ่ายคนละสกุล
    const chosen = holdings.find((item) => item.id === id);
    if (chosen !== undefined) {
      setCurrency(chosen.currency);
    }
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (result.state === 'ok') {
      onSubmit(result.dividend);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4">
      <form
        onSubmit={handleSubmit}
        className="my-8 w-full max-w-md rounded-[14px] border border-line bg-ink p-6"
      >
        <h2 className="text-lg font-semibold text-ink-100">บันทึกปันผล</h2>
        <p className="mt-1 text-xs text-ink-500">กรอกยอดที่เข้าบัญชีจริง</p>

        <div className="mt-5 space-y-4">
          <label className="block">
            <span className="mb-1 block text-sm text-ink-200">สินทรัพย์</span>
            <select
              className={inputClass}
              value={holdingId}
              onChange={(event) => chooseHolding(event.target.value)}
            >
              {holdings.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.symbol} · {item.platform}
                </option>
              ))}
            </select>
          </label>

          <div className="grid grid-cols-[1fr_6rem] gap-2">
            <label className="block">
              <span className="mb-1 block text-sm text-ink-200">ยอดปันผล</span>
              <input
                className={inputClass}
                inputMode="decimal"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                autoFocus
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm text-ink-200">สกุลเงิน</span>
              <input
                className={`${inputClass} font-mono`}
                value={currency}
                maxLength={3}
                onChange={(event) => setCurrency(event.target.value.toUpperCase())}
              />
            </label>
          </div>

          <label className="block">
            <span className="mb-1 block text-sm text-ink-200">โน้ต (ไม่บังคับ)</span>
            <input
              className={inputClass}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </label>
        </div>

        {result.state === 'error' && (
          <p
            role="alert"
            className="mt-5 rounded-lg border border-down/25 bg-down/10 px-3.5 py-2.5 text-xs leading-relaxed text-down"
          >
            {result.message}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-line-strong px-4 py-2 text-sm text-ink-300 transition hover:text-ink-100"
          >
            ยกเลิก
          </button>
          <button
            type="submit"
            disabled={result.state !== 'ok' || isSaving}
            className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-ink-100 transition hover:brightness-110 disabled:opacity-50"
          >
            {isSaving ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
        </div>
      </form>
    </div>
  );
}
